import React from 'react';
import { action, observable, runInAction } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { IRolodexContactDTO, IRolodexDAO, TUpdateRolodexContactRequestParams } from '../../../../../api/rolodex.dao';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { alertServerError, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { IntlShape } from 'react-intl';
import { FormFieldProps, InitialData } from '../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../infrastructure/implementations/form-viewmodel-controller';
import { rolodexContactConfigList, RolodexContactFieldKey, RolodexContactInitialData } from './rolodex-contact-form.config';
import { TObject } from '../../../../../infrastructure/type-extensions';
import { AUDIO_ADDED_TO_CONTACT_TEXT, AUDIO_FOR_THIS_CONTACT_TEXT, AUDIO_REMOVED_FROM_THE_CONTACT_TEXT, CONTACT_UPDATE_SUCCESS_TEXT } from './rolodex-contact-text';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { isTrackingFieldChanged, RolodexContactEvents } from '../../../../../tracking-service/tracking-values';
import UploadAudioS3VM from '../upload-audio/upload-audio-to-s3.viewmodel';
import { TNamePronunciationPopupMode } from './name-pronunciation-popup/name-pronunciation-popup';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { ARE_YOU_SURE_YOU_WANT_TO } from '../../../../../constants/common-confirm-text.constant';
import { Space } from '../../../../../constants/html-character-entities';
import { DELETE_TEXT, YES_DELETE_TEXT } from '../../../../../constants/common-labels.constants';

class RolodexContactFormVM {
    private static createFormVM(initialData: InitialData): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'RolodexContactFormVM', rolodexContactConfigList);
            }
        }();
    }

    private static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, Container.get(IErrorParsing).extractMessage(e));
    }

    private static hasStringChanged(from: string | null | undefined, to: string | null | undefined): boolean {
        return (from || '') !== (to || '');
    }

    private static hasBooleanChanged(from: boolean | null | undefined, to: boolean | null | undefined): boolean {
        return !!from !== !!to;
    }

    @observable
    public isLoading: boolean = false;
    @observable
    public isSaving: boolean = false;
    @observable
    private formDataCtrl: AbstractFormViewModelController = null;
    @observable
    private rolodexContactSnapshot: IRolodexContactDTO = null;
    @observable
    private uploadAudioS3VM: UploadAudioS3VM = null;
    @observable
    public namePronunciationPopupMode: TNamePronunciationPopupMode = null;

    constructor(
        private readonly contactId: number,
        public readonly intl: IntlShape,
        private readonly saveCompletedCallback: (contact: IRolodexContactDTO) => void,
        @Inject private readonly rolodexDAO: IRolodexDAO = Container.get(IRolodexDAO),
        @Inject protected readonly trackingService: TrackingServiceProvider = Container.get(TrackingServiceProvider),
        @Inject private readonly alertsService: IAlertsService = Container.get(IAlertsService),
    ) {

    }

    public get formVM(): AbstractFormViewModelController { return this.formDataCtrl; }
    public get contactSnapshot(): IRolodexContactDTO { return this.rolodexContactSnapshot; }
    public get uploadAudioVM(): UploadAudioS3VM { return this.uploadAudioS3VM; }
    public get loading(): boolean { return this.isLoading; }
    public get saving(): boolean { return this.isSaving; }
    public get submitDisabled(): boolean {
        return this.isSaving || !!this.uploadAudioS3VM?.isAudioUploading || !!this.uploadAudioS3VM?.isAudioChecking;
    }

    public getFormFieldPropsByKey = (key: string): FormFieldProps => {
        return {
            value: this.formDataCtrl.getValue(key),
            setValue: value => this.formDataCtrl.setValue(key, value),
            setTouched: () => this.formDataCtrl.setValueTouched(key),
            ...this.formDataCtrl.getValueValidationState(key),
        } as FormFieldProps;
    };

    public getFormFields = (keys: TObject<string>): TObject<FormFieldProps> => {
        const formFields = {};
        for (const key of Object.values(keys)) {
            formFields[key] = this.getFormFieldPropsByKey(key);
        }
        return formFields;
    };

    @action
    public initialize = async (): Promise<void> => {
        await this.getRolodexContactDetails();
        this.formDataCtrl = RolodexContactFormVM.createFormVM(this.composeRolodexContactInitialProps());
        this.uploadAudioS3VM = new UploadAudioS3VM(this.contactId, this.rolodexContactSnapshot?.nameAudio);
    };

    public submitForm = (): Promise<boolean | undefined> | undefined => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
        } else {
            return this.editRolodexContact();
        }
    };

    @action
    public setNamePronunciationPopupMode = (mode: TNamePronunciationPopupMode): void => {
        this.namePronunciationPopupMode = mode;
    };

    @action
    public uploadAudioFileToContact = async (file?: File | null): Promise<boolean> => {
        if (this.isSaving) return false;
        const isDeletingAudio = file === null;
        try {
            runInAction(() => {
                this.isSaving = true;
            });
            if (file && !(await this.uploadAudioS3VM.setAudio(file))) {
                if (this.uploadAudioS3VM.audioError) {
                    alertServerError('Error', this.uploadAudioS3VM.audioError);
                }
                return false;
            }
            const dto = this.createAudioUpdateDTO(isDeletingAudio);
            const contact = await this.rolodexDAO.updateRolodexContact(dto);
            this.handleAudioUpdateSuccess(contact, isDeletingAudio);
            this.showAudioUpdateNotification(isDeletingAudio);
            this.trackAudioUpdate(isDeletingAudio);
            return true;
        } catch (error) {
            RolodexContactFormVM.handleApiException(error);
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    public closeNamePronunciationPopup = (): void => {
        if (this.isSaving) {
            return;
        }
        this.uploadAudioVM.recoverAudioFile();
        this.setNamePronunciationPopupMode(null);
    };

    @action
    private editRolodexContact = async (): Promise<boolean | undefined> => {
        try {
            runInAction(() => {
                this.isSaving = true;
            });
            const dto = {
                ...this.toRolodexContactTemplateDTO(),
                contactId: this.contactSnapshot.contactId,
            };
            const updatedContact = await this.rolodexDAO.updateRolodexContact(dto);
            this.setNamePronunciationPopupMode(null);
            showNotification(this.intl.formatMessage(CONTACT_UPDATE_SUCCESS_TEXT));
            this.trackEventForMixPanel('Edit OE Contact Profile', dto);
            this.saveCompletedCallback(updatedContact);
            return true;
        } catch (e) {
            RolodexContactFormVM.handleApiException(e);
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    public onDeleteNamePronunciationAudioFile = (): void => {
        if (this.uploadAudioS3VM.hasNewlyUploadedAudio()) {
            this.uploadAudioS3VM.recoverAudioFile();
            return;
        }
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    {this.intl.formatMessage(ARE_YOU_SURE_YOU_WANT_TO)}
                    <Space />
                    <span className='alert-message--danger'>
                        {this.intl.formatMessage(DELETE_TEXT)}
                    </span>
                    <Space />
                    <span>
                    {this.intl.formatMessage(AUDIO_FOR_THIS_CONTACT_TEXT)}
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: this.intl.formatMessage(YES_DELETE_TEXT),
                modifier: 'confirm-warn',
                handler: this.deleteNamePronunciationAudioFile
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @action
    private async getRolodexContactDetails(): Promise<void> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });
            const contact = await this.rolodexDAO.getContact({ contactId: this.contactId });
            runInAction(() => {
                this.rolodexContactSnapshot = contact;
            });
        } catch (error) {
            RolodexContactFormVM.handleApiException(error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    private composeRolodexContactInitialProps(): RolodexContactInitialData {
        return {
            [RolodexContactFieldKey.FIRST_NAME]: this.rolodexContactSnapshot?.firstName || '',
            [RolodexContactFieldKey.LAST_NAME]: this.rolodexContactSnapshot?.lastName || '',
            [RolodexContactFieldKey.PREFERRED_NAME]: this.rolodexContactSnapshot?.preferredName || '',
            [RolodexContactFieldKey.MIDDLE_NAME]: this.rolodexContactSnapshot?.middleName || '',
            [RolodexContactFieldKey.NAME_PREFIX]: this.rolodexContactSnapshot?.namePrefix || '',
            [RolodexContactFieldKey.NAME_SUFFIX]: this.rolodexContactSnapshot?.nameSuffix || '',
            [RolodexContactFieldKey.NAME_NOTES]: this.rolodexContactSnapshot?.nameNotes || '',
            [RolodexContactFieldKey.NAME_PHONETIC]: this.rolodexContactSnapshot?.namePhonetic || '',
            [RolodexContactFieldKey.TITLE]: this.rolodexContactSnapshot?.title || '',
            [RolodexContactFieldKey.PHONE_DIRECT]: this.rolodexContactSnapshot?.phoneDirect || '',
            [RolodexContactFieldKey.PHONE_MOBILE]: this.rolodexContactSnapshot?.phoneMobile || '',
            [RolodexContactFieldKey.LINKEDIN_URL]: this.rolodexContactSnapshot?.linkedinUrl || '',
            [RolodexContactFieldKey.ACTIVE_STATUS]: this.rolodexContactSnapshot?.activeStatus || '',
            [RolodexContactFieldKey.VIP_FLAG]: !!this.rolodexContactSnapshot?.vipFlag,
        };
    }

    private toRolodexContactTemplateDTO(): TUpdateRolodexContactRequestParams {
        const audio = this.uploadAudioS3VM?.getAudioToApi();
        return {
            contactId: this.contactSnapshot.contactId,
            firstName: (this.formDataCtrl.getValue(RolodexContactFieldKey.FIRST_NAME) as string).trim(),
            lastName: (this.formDataCtrl.getValue(RolodexContactFieldKey.LAST_NAME) as string).trim(),
            preferredName: (this.formDataCtrl.getValue(RolodexContactFieldKey.PREFERRED_NAME) as string).trim(),
            namePrefix: (this.formDataCtrl.getValue(RolodexContactFieldKey.NAME_PREFIX) as string).trim(),
            middleName: (this.formDataCtrl.getValue(RolodexContactFieldKey.MIDDLE_NAME) as string).trim(),
            nameSuffix: (this.formDataCtrl.getValue(RolodexContactFieldKey.NAME_SUFFIX) as string).trim(),
            nameNotes: (this.formDataCtrl.getValue(RolodexContactFieldKey.NAME_NOTES) as string).trim(),
            namePhonetic: (this.formDataCtrl.getValue(RolodexContactFieldKey.NAME_PHONETIC) as string).trim(),
            title: (this.formDataCtrl.getValue(RolodexContactFieldKey.TITLE) as string).trim(),
            phoneDirect: (this.formDataCtrl.getValue(RolodexContactFieldKey.PHONE_DIRECT) as string).trim(),
            phoneMobile: (this.formDataCtrl.getValue(RolodexContactFieldKey.PHONE_MOBILE) as string).trim(),
            linkedinUrl: (this.formDataCtrl.getValue(RolodexContactFieldKey.LINKEDIN_URL) as string).trim(),
            activeStatus: (this.formDataCtrl.getValue(RolodexContactFieldKey.ACTIVE_STATUS) as string).trim(),
            ...(audio && { nameAudio: audio }),
            vipFlag: (this.formDataCtrl.getValue(RolodexContactFieldKey.VIP_FLAG) as boolean),
        };
    }

    private trackEventForMixPanel(eventName: RolodexContactEvents, updatedContact: TUpdateRolodexContactRequestParams) {
        this.trackingService.get().track(eventName, {
            'Contact ID': updatedContact.contactId,
            'First Name': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.firstName, updatedContact.firstName)
            ),
            'Last Name': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.lastName, updatedContact.lastName)
            ),
            'Title': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.title, updatedContact.title)
            ),
            'Preferred Name': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.preferredName, updatedContact.preferredName)
            ),
            'Middle Name': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.middleName, updatedContact.middleName)
            ),
            'Name Prefix': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.namePrefix, updatedContact.namePrefix)
            ),
            'Name Suffix': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.nameSuffix, updatedContact.nameSuffix)
            ),
            'Name Notes': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.nameNotes, updatedContact.nameNotes)
            ),
            'Name Phonetic': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.namePhonetic, updatedContact.namePhonetic)
            ),
            'Active Status': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.activeStatus, updatedContact.activeStatus)
            ),
            'Direct Phone': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.phoneDirect, updatedContact.phoneDirect)
            ),
            'Mobile Phone': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.phoneMobile, updatedContact.phoneMobile)
            ),
            'LinkedIn Url': isTrackingFieldChanged(
                RolodexContactFormVM.hasStringChanged(this.contactSnapshot.linkedinUrl, updatedContact.linkedinUrl)
            ),
            'VIP Status': isTrackingFieldChanged(
                RolodexContactFormVM.hasBooleanChanged(this.contactSnapshot.vipFlag, updatedContact.vipFlag)
            ),
        });
    }

    private deleteNamePronunciationAudioFile = async (): Promise<void> => {
        await this.uploadAudioFileToContact(null);
    };

    private toRolodexContactTemplateDTOFromSnapshot(): TUpdateRolodexContactRequestParams {
        return {
            contactId: this.contactSnapshot.contactId,
            firstName: this.rolodexContactSnapshot.firstName,
            lastName: this.rolodexContactSnapshot.lastName,
            preferredName: this.rolodexContactSnapshot.preferredName,
            namePrefix: this.rolodexContactSnapshot.namePrefix,
            middleName: this.rolodexContactSnapshot.middleName,
            nameSuffix: this.rolodexContactSnapshot.nameSuffix,
            nameNotes: this.rolodexContactSnapshot.nameNotes,
            namePhonetic: this.rolodexContactSnapshot.namePhonetic,
            title: this.rolodexContactSnapshot.title,
            phoneDirect: this.rolodexContactSnapshot.phoneDirect,
            phoneMobile: this.rolodexContactSnapshot.phoneMobile,
            linkedinUrl: this.rolodexContactSnapshot.linkedinUrl,
            activeStatus: this.rolodexContactSnapshot.activeStatus,
            vipFlag: this.rolodexContactSnapshot.vipFlag,
        };
    }

    private trackUploadAudioForMixPanel(eventName: RolodexContactEvents): void {
        this.trackingService.get().track(eventName, {
            'Contact ID': this.contactSnapshot.contactId,
            'Source': 'Central',
        });
    }

    private createAudioUpdateDTO(
        isDeletingAudio: boolean,
    ): TUpdateRolodexContactRequestParams {
        const audio = isDeletingAudio
            ? { title: '', objectKey: '' }
            : this.uploadAudioS3VM?.getAudioToApi()
        ;

        return {
            ...this.toRolodexContactTemplateDTOFromSnapshot(),
            contactId: this.contactSnapshot.contactId,
            ...(audio ? { nameAudio: audio } : {}),
        };
    }

    @action
    private handleAudioUpdateSuccess(
        contact: IRolodexContactDTO,
        isDeletingAudio: boolean,
    ): void {
        this.namePronunciationPopupMode = null;
        if (isDeletingAudio) {
            this.uploadAudioS3VM.completeAudioRemoval();
        } else {
            this.uploadAudioS3VM.updateStoredAudio(contact.nameAudio);
        }
    }

    private showAudioUpdateNotification(isDeletingAudio: boolean): void {
        const message = isDeletingAudio
            ? AUDIO_REMOVED_FROM_THE_CONTACT_TEXT
            : AUDIO_ADDED_TO_CONTACT_TEXT
        ;
        showNotification(this.intl.formatMessage(message));
    }

    private trackAudioUpdate(isDeletingAudio: boolean): void {
        const eventName: RolodexContactEvents = isDeletingAudio
            ? 'Remove Contact Pronunciation'
            : 'Record Contact Pronunciation'
        ;
        this.trackUploadAudioForMixPanel(eventName);
    }
}

export {
    RolodexContactFormVM
};